import { useEffect } from 'react';
import { pageCount, pageItems, shownRange } from '../../utils/pagination';
import styles from './Pagination.module.css';
import Icon from './Icon';

interface PaginationProps {
  page: number;
  pageSize: number;
  // Rows on every page together, as the server counted them.
  total: number;
  onPageChange: (page: number) => void;
  // Plural name of a row, e.g. "folders".
  noun?: string;
  // 'bar' closes a table card (top border, card padding); 'inline' sits inside a padded
  // panel; 'card' stands alone under a grid of cards.
  variant?: 'bar' | 'inline' | 'card';
}

const fmt = (n: number) => n.toLocaleString('en-US');

const Pagination = ({ page, pageSize, total, onPageChange, noun, variant = 'bar' }: PaginationProps) => {
  const count = pageCount(total, pageSize);
  // A page past the end — an old ?page= link, or rows deleted since — moves to the last page.
  useEffect(() => {
    if (total > 0 && page > count) onPageChange(count);
  }, [total, page, count, onPageChange]);
  if (total <= 0) return null;
  const { from, to } = shownRange(page, pageSize, total);
  const go = (p: number) => {
    const next = Math.min(count, Math.max(1, p));
    if (next !== page) onPageChange(next);
  };

  return (
    <nav className={`${styles.pager} ${variant === 'bar' ? '' : styles[variant]}`} aria-label="Pagination">
      <p className={styles.summary}>
        {`Showing ${fmt(from)}–${fmt(to)} of ${fmt(total)}${noun ? ` ${noun}` : ''}`}
      </p>
      <div className={styles.controls}>
        <button type="button" className={styles.step} onClick={() => go(page - 1)} disabled={page <= 1}>
          <Icon name="chevron_left" aria-hidden="true" />
          Previous
        </button>
        <span className={styles.pages}>
          {pageItems(page, count).map((item, i) => (item === 'gap' ? (
            <span key={`gap-${i}`} className={styles.gap} aria-hidden="true">…</span>
          ) : (
            <button
              key={item}
              type="button"
              className={`${styles.pageBtn} ${item === page ? styles.current : ''}`}
              aria-label={`Go to page ${item}`}
              aria-current={item === page ? 'page' : undefined}
              onClick={() => go(item)}
            >
              {fmt(item)}
            </button>
          )))}
        </span>
        <span className={styles.pageOf}>Page {fmt(page)} of {fmt(count)}</span>
        <button type="button" className={styles.step} onClick={() => go(page + 1)} disabled={page >= count}>
          Next
          <Icon name="chevron_right" aria-hidden="true" />
        </button>
      </div>
    </nav>
  );
};

export default Pagination;
